'use server';

import { revalidatePath } from 'next/cache';
import { auth } from '@clerk/nextjs/server';

const NEXT_PUBLIC_BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://backend:8500';

interface SystemPrompt {
  id: string;
  agent_id: string;
  language_code: string;
  content: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
}

interface SystemPromptsDict {
  prompts: Record<string, string>; // { ko: '...', en: '...', ja: '...', zh: '...' }
}

/**
 * 에이전트의 모든 언어별 시스템 프롬프트 조회
 */
export async function getSystemPrompts(agentId: string): Promise<SystemPrompt[]> {
  const { userId } = await auth();

  if (!userId) {
    throw new Error('Unauthorized');
  }

  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/system-prompts`,
    {
      headers: {
        'X-User-Id': userId,
      },
      next: {
        revalidate: 60,
        tags: [`system-prompts-${agentId}`],
      },
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to fetch system prompts: ${error}`);
  }

  return response.json();
}

/**
 * 에이전트의 언어별 시스템 프롬프트를 딕셔너리 형태로 조회
 */
export async function getSystemPromptsDict(
  agentId: string
): Promise<SystemPromptsDict> {
  const { userId } = await auth();

  if (!userId) {
    throw new Error('Unauthorized');
  }

  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/system-prompts/dict`,
    {
      headers: {
        'X-User-Id': userId,
      },
      cache: 'no-store',
      next: {
        // revalidate: 60,
        tags: [`system-prompts-${agentId}`],
      },
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to fetch system prompts dict: ${error}`);
  }

  return response.json();
}

/**
 * 특정 언어의 시스템 프롬프트 조회
 */
export async function getSystemPromptByLanguage(
  agentId: string,
  languageCode: string
): Promise<SystemPrompt> {
  const { userId } = await auth();

  if (!userId) {
    throw new Error('Unauthorized');
  }

  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/system-prompts/${languageCode}`,
    {
      headers: {
        'X-User-Id': userId,
      },
      next: {
        revalidate: 60,
        tags: [`system-prompts-${agentId}`],
      },
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to fetch system prompt: ${error}`);
  }

  return response.json();
}

/**
 * 새로운 언어의 시스템 프롬프트 생성
 */
export async function createSystemPrompt(
  agentId: string,
  languageCode: string,
  content: string
): Promise<SystemPrompt> {
  const { userId } = await auth();

  if (!userId) {
    throw new Error('Unauthorized');
  }

  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/system-prompts`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-User-Id': userId,
      },
      body: JSON.stringify({
        agent_id: agentId,
        language_code: languageCode,
        content,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to create system prompt: ${error}`);
  }

  const result = await response.json();

  // 캐시 무효화
  revalidatePath(`/agent/${agentId}`);
  revalidatePath('/dashboard');

  return result;
}

/**
 * 기존 시스템 프롬프트 수정
 */
export async function updateSystemPrompt(
  agentId: string,
  languageCode: string,
  content: string
): Promise<SystemPrompt> {
  const { userId } = await auth();

  if (!userId) {
    throw new Error('Unauthorized');
  }

  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/system-prompts/${languageCode}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-User-Id': userId,
      },
      body: JSON.stringify({
        content,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to update system prompt: ${error}`);
  }

  const result = await response.json();

  // 캐시 무효화
  revalidatePath(`/agent/${agentId}`);
  revalidatePath('/dashboard');

  return result;
}

/**
 * 여러 언어의 시스템 프롬프트를 한 번에 생성/수정 (Batch)
 */
export async function createOrUpdateSystemPromptsBatch(
  agentId: string,
  prompts: Record<string, string>
): Promise<SystemPrompt[]> {
  const { userId } = await auth();

  if (!userId) {
    throw new Error('Unauthorized');
  }

  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/system-prompts/batch`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-User-Id': userId,
      },
      body: JSON.stringify({
        prompts,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to batch create/update system prompts: ${error}`);
  }

  const result = await response.json();

  // 캐시 무효화
  revalidatePath(`/agent/${agentId}`);
  revalidatePath('/dashboard');

  return result;
}

/**
 * 시스템 프롬프트 삭제
 */
export async function deleteSystemPrompt(
  agentId: string,
  languageCode: string,
  permanent: boolean = false
): Promise<{ message: string; language_code: string }> {
  const { userId } = await auth();

  if (!userId) {
    throw new Error('Unauthorized');
  }

  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/system-prompts/${languageCode}?permanent=${permanent}`,
    {
      method: 'DELETE',
      headers: {
        'X-User-Id': userId,
      },
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to delete system prompt: ${error}`);
  }

  const result = await response.json();

  // 캐시 무효화
  revalidatePath(`/agent/${agentId}`);
  revalidatePath('/dashboard');

  return result;
}

/**
 * 시스템 프롬프트 번역 (자동 번역)
 */
interface TranslationResult {
  language_code: string;
  content: string;
  confidence: number;
  improved: boolean;
  meaning_preserved: boolean;
}

interface TranslateResponse {
  success: boolean;
  source_language_code: string;
  source_content: string;
  translations: Record<string, TranslationResult>;
  message?: string;
  error?: string;
}

export async function translateSystemPrompt(
  agentId: string,
  sourceLanguageCode: string,
  sourceContent: string,
  targetLanguageCodes: string[]
): Promise<TranslateResponse> {
  const { userId } = await auth();

  if (!userId) {
    throw new Error('Unauthorized');
  }

  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/system-prompts/translate`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-User-Id': userId,
      },
      body: JSON.stringify({
        source_language_code: sourceLanguageCode,
        source_content: sourceContent,
        target_language_codes: targetLanguageCodes,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to translate system prompt: ${error}`);
  }

  return response.json();
}

/**
 * 시스템 프롬프트 번역 Job 시작 (비동기, 병렬 처리)
 *
 * 즉시 job_id를 반환하고 백그라운드에서 모든 언어를 병렬로 번역합니다.
 * 클라이언트는 getJobStatus(jobId)로 진행 상태를 폴링할 수 있습니다.
 */
interface StartTranslationJobResponse {
  success: boolean;
  job_id: string;
  message: string;
}

export async function startTranslationJob(
  agentId: string,
  sourceLanguageCode: string,
  sourceContent: string,
  targetLanguageCodes: string[]
): Promise<StartTranslationJobResponse> {
  const { userId } = await auth();

  if (!userId) {
    throw new Error('Unauthorized');
  }

  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/system-prompts/translate/start`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-User-Id': userId,
      },
      body: JSON.stringify({
        source_language_code: sourceLanguageCode,
        source_content: sourceContent,
        target_language_codes: targetLanguageCodes,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to start translation job: ${error}`);
  }

  return response.json();
}
